<template>
  <div class="collection-header" :class="classes">
    <Media v-if="images" :images="images" cover class="image" />
    <div class="info">
      <h1 class="title">{{ title }}</h1>
      <p class="description">{{ description }}</p>
    </div>

    <ProductTypes
      v-if="shouldShowProductTypes"
      class="types"
      @change="(value) => $emit('typeChange', value)"
    />
  </div>
</template>

<script>
import Media from "@/components/Media.vue";
import ProductTypes from "@/components/collection/ProductTypes.vue";
import { JEWELLERY_COLLECTION_HANDLE } from "@/helpers/jewelleryGroups";
import { mapGetters, mapState } from "vuex";

export default {
  name: "CollectionHeader",
  components: {
    Media,
    ProductTypes,
  },
  emits: ["typeChange"],
  computed: {
    ...mapState("collection", ["collection", "loading"]),
    ...mapGetters("collection", ["types"]),
    classes() {
      return {
        "collection-header--no-image": !this.images,
        "collection-header--no-types": !this.shouldShowProductTypes,
        "collection-header--loading": this.loading,
      };
    },
    shouldShowProductTypes() {
      return (
        this.collection?.handle === JEWELLERY_COLLECTION_HANDLE ||
        this.types?.length > 1
      );
    },
    title() {
      return this.collection.title;
    },
    description() {
      return this.collection.description;
    },
    images() {
      return this.collection.image?.reference.image;
    },
  },
};
</script>

<style lang="scss" scoped>
.collection-header {
  text-align: center;

  @include respond-above(lg) {
    display: grid;
    grid-template-areas:
      "image info"
      "image info"
      "types types";
    grid-template-columns: 1fr minmax(400px, 1fr);
    row-gap: 20px;
  }

  @include respond-above(xl) {
    display: grid;
    grid-template-areas:
      "image info"
      "image info"
      "types types";
    grid-template-columns: 1fr minmax(624px, 1fr);
    row-gap: 20px;
  }

  .info {
    grid-area: info;
    padding: 24px;

    @include respond-above(lg) {
      background-color: $color-grey-lightest;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding-right: 120px;
      padding-left: 120px;
    }
  }

  .title {
    @include editorial-headline(20px);
    text-transform: uppercase;
    margin-bottom: 16px;

    @include respond-above(md) {
      @include editorial-headline(28px);
    }
  }

  .description {
    margin: 8px auto 4px;
    @include editorial-body(14px);

    @include respond-above(md) {
      margin: 8px 0 0;
      @include editorial-body(16px);
    }
  }

  .image {
    grid-area: image;
    justify-self: end;
    @include respond-above(md) {
      height: 400px;
      width: 100%;
      overflow: hidden;
    }

    img {
      max-width: 100%;
      height: auto;
    }
  }

  .types {
    grid-area: types;
    align-self: start;

    @include respond-above(md) {
      padding-left: 100px;
      padding-bottom: 54px;
    }
  }

  &--no-types {
    @include respond-above(md) {
      grid-template-areas: "image info";
    }

    .info {
      padding: 24px;

      @include respond-above(md) {
        padding: 54px 100px;
      }
    }
  }

  &--no-image {
    grid-template-columns: 1fr;
    grid-template-areas:
      "info"
      "types";
    justify-content: center;
    text-align: center;
    column-gap: 0;

    .info {
      background-color: $color-white;
      margin: 0 auto;

      @include respond-above(md) {
        max-width: 669px;
        padding-right: 0;
        padding-left: 0;
        padding-bottom: 0;
      }
    }

    .types {
      @include respond-above(md) {
        justify-content: center;
        padding-right: 100px;
        padding-left: 100px;
      }
    }

    .description {
      margin-left: auto;
      margin-right: auto;
    }
  }

  &--loading {
    .image {
      display: block;
      aspect-ratio: 5 / 3;
      background: #ffffff;
      width: 100%;
    }
  }
}
</style>
